import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ExpandableSection from "../../components/ExpandableContent/ExpandableContent";
import pdf from "../../assets/local_hattiesburg_attractions.pdf";
import Tooltip from "../../components/Tooltip/Tooltip";

const SummerWorkshop2026Application = ({ defaultTab, deadline }) => {
  const navigate = useNavigate();
  const initialTab = defaultTab >= 0 && defaultTab < 4 ? defaultTab : 0;
  const [tabValue, setTabValue] = useState(initialTab);
  const presentDate = new Date();
  const deadlineDate = new Date(deadline);
  const isExpired = presentDate > deadlineDate;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  useEffect(() => {
    document.title = "Summer Residency Application 2026 - MS Digital Hub";
  }, []);

  return (
    <Box
      sx={{
        width: "auto",

        padding: "0px 20px",
        margin: "0px auto",
        padding: { xs: "20px", sm: "30px", md: "1px 80px" },
        border: "none",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
    >
      <Box
        my={4}
        sx={{
          gap: 4,
          alignItems: "center",
        }}
      >
        <Box>
          <Box sx={{}}>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                textAlign: "center",
                marginBottom: "30px",
              }}
            >
              Apply to Our Digitization Summer Residency Program 2026
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#FFD700",
                  color: "#111111",
                  padding: "12px 20px",
                  fontWeight: "bold",
                  "&:hover": { backgroundColor: "#FFB800" },
                }}
                href="https://usmforms.formstack.com/forms/2026_hub_residency"
                target="_blank"
                disabled={isExpired}
              >
                {isExpired ? "Deadline passed" : "Apply"}
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: {
                xs: "center",
                sm: "center",
                md: "flex-start",
                lg: "flex-start",
              },
              justifyContent: "space-between",
              padding: "20px",
              gap: { xs: "30px", sm: "30px" },
              flexDirection: {
                xs: "column",
                sm: "column",
                md: "row",
                lg: "row",
              },
            }}
          >
            <Typography variant="body1" sx={{ flex: 1, textAlign: "center" }}>
              <strong>
                Dates
                <br />
              </strong>{" "}
              Monday, June 15, 2026 - Friday, July 24, 2026
            </Typography>
            <Typography variant="body1" sx={{ flex: 1, textAlign: "center" }}>
              <strong>
                Location
                <br />
              </strong>{" "}
              University of Southern Mississippi - Hattiesburg campus
            </Typography>
            <Typography variant="body1" sx={{ flex: 1, textAlign: "center" }}>
              <strong>
                Deadline
                <br />
              </strong>{" "}
              Application closes: Thursday, April 2, 2026 at 11:59PM (CT)
              <br />
              *Note: Late applications will not be accepted.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Paper
        square
        elevation={0}
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          aria-label="STEP page navigation"
          sx={{ flexWrap: "flex-wrap sm:flex-nowrap" }}
        >
          <Tab label="About" sx={{ fontWeight: "bold" }} />
          <Tab label="Description" sx={{ fontWeight: "bold" }} />
          <Tab label="Qualification" sx={{ fontWeight: "bold" }} />
        </Tabs>
      </Paper>

      <Box my={4}>
        {tabValue === 0 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
              About
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: "1.8" }} paragraph>
              Learn how to tell stories about Mississippi’s history and culture
              using the tools of the digital humanities. Students at all
              Mississippi universities and community colleges are invited to
              apply for a six-week summer residency hosted by The University of
              Southern Mississippi’s Digital Humanities Hub. Participants will
              learn the basics of digitization and the digital humanities from
              experts affiliated with the Center for Digital Humanities at
              Southern Miss and the Mississippi Digital Library.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: "1.8" }} paragraph>
              Digital Humanities is an evolving field that uses computers to
              explore scholarly questions in the humanities (English, history,
              philosophy and similar subjects). Digital Humanists use a wide
              range of tools, such as mapping, data visualization, text
              analysis, digital exhibits, and digital storytelling, to advance
              research, interpret cultural materials, and raise new questions
              about the past and present.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: "1.8" }} paragraph>
              The six-week residential course for Mississippi students only will
              take place from Monday, June 15, 2026, through Friday, July 24,
              2026. Applications open January 22, 2026, and are due by 11:59 PM
              (CT) on March 31, 2026. Participation is limited to ten students.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: "1.8" }} paragraph>
              All participants will be provided with housing, most meals, travel
              reimbursement, and small stipends. You must attend in person.
            </Typography>
          </Box>
        )}
        {tabValue === 1 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
              Description
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: "1.8" }} paragraph>
              With financial support from the National Historical Publications
              and Records Commission (part of the National Archives), the
              Mississippi Digital Humanities Hub supports efforts to train
              Mississippians to become digital humanists. This six-week course
              will introduce you to the basics of the digital humanities, an
              approach to research that leverages digital tools to preserve and
              interpret history, literature, philosophy, and other scholarly
              fields, and then use those skills to tell digital stories.
              Participants will develop their own research and writing projects
              related to the history, literature, politics, and culture of
              Mississippi and its people, learn about digital tools from
              artificial intelligence to mapping to network analysis, work
              collaboratively (with faculty and fellow workshop participants) on
              projects, and present them virtually and in person. Along the way,
              you will discover how the documentary history of Mississippi is
              stored and preserved, how to collect and clean data, and how
              digital scholarship can reveal patterns that are not always
              immediately apparent.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: "1.8" }} paragraph>
              In addition to doing digital scholarship, students enrolled in the
              summer residency will explore careers that benefit from digital
              scholarship, learn life skills such as how to prepare an effective
              resume, and have opportunities to explore the cultural and
              historical sites of Hattiesburg and the surrounding communities.
            </Typography>
          </Box>
        )}
        {tabValue === 2 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
              Qualification
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: "1.8" }} paragraph>
              Applicants must be interested in digital humanities, currently
              enrolled at a two- or four-year college or university in
              Mississippi, and returning to college in fall 2026 (current
              first-year students, sophomores, and juniors). Applicants must be
              either U.S. citizens, U.S. nationals, or permanent residents of
              the U.S. We strongly encourage applications from students at
              colleges and universities with student populations who are
              underrepresented in digital humanities. This includes women, BIPOC
              students, first-generation students, student-veterans or active
              military personnel, and those from underprivileged backgrounds.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: "1.8" }} paragraph>
              If selected, participants cannot hold another obligation—such as
              being enrolled in another fellowship, attending online courses, or
              holding a part/full-time job. Workshop residents are expected to
              participate in-person on the campus of The University of Southern
              Mississippi and attend all sessions and group activities. There
              will not be a remote option for this workshop.
            </Typography>
          </Box>
        )}

        {tabValue === 3 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
              Contact
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: "1.8" }} paragraph>
              Contact Andrew McSorley, Digital Humanities Postdoctoral Scholar
              (Andrew.mcsorley@usm.edu) with any questions about the workshop or
              the application process.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SummerWorkshop2026Application;
